import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  selectSignUpInvite,
  selectSignUpUser,
} from '../../../redux/auth/selectors';

import { withRouter } from 'react-router-dom';

import { debounce, get } from 'lodash';

import { ssoLogin } from '../../../redux/auth/actions';

import constants from '../../../constants';

import {
  debounceWrapper,
  fixVirtualKeyboardPadding,
  removeVirtualKeyboardPadding,
} from '../../../util/util';

import { EMAIL_CONFLICT, USER_PROPERTY_CONFLICT } from '../config';

import containerStyles from './SignInForm.scss';
import qmStyles from '../../../theme/qmStyles';

import NewAccountFooter from './NewAccountFooter';
import SSOContainer from '../../../components/SSOContainer';
import Google from '../../../components/SignOnButtons/Google';
import Apple from '../../../components/SignOnButtons/Apple';
import Facebook from '../../../components/SignOnButtons/Facebook';
import FormInput from '../../../components/FormInput';
import Clickable from '../../../components/Clickable';
import SecuredFormInput from '../../../components/Form/SecuredFormInput';
import Loader from '../../../components/Loader';

import API from '../../../services/API';
import { selectMe } from '../../../redux/me/selectors';
import GenericInlineError from '../../../components/GenericInlineError';
import { getIdentityProviders } from '../../../services/singleSignOn';

const inputs = ['email', 'password'];

class SignInForm extends Component {
  static propTypes = {
    location: PropTypes.shape({ state: PropTypes.object }).isRequired,
    history: PropTypes.object.isRequired,

    inviteData: PropTypes.object,

    onSubmit: PropTypes.func.isRequired,
    navigateNext: PropTypes.func,
  };

  static defaultProps = {
    inviteData: null,

    navigateNext: () => {},
  };

  constructor(props) {
    super(props);

    inputs.forEach(input => {
      this[input] = React.createRef();
    });

    this.postData = debounce(this.postData, 1337, {
      leading: true,
      trailing: false,
    });
  }

  state = {
    email: '',
    password: '',
    passwordIncorrect: false,
    addPadding: false,
    signInError: false,
    google_url: null,
    facebook_url: null,
    apple_url: null,
    google_enabled: false,
    loadingSignOn: this.props.location.search.includes('code'),
  };

  componentDidMount() {
    inputs.forEach(input => {
      fixVirtualKeyboardPadding(
        this[input].current,
        () => this.resizeContainer(true),
        () => this.resizeContainer(false),
      );
    });

    // @todo - switch to common package when https://bitbucket.org/quartermasterx/desktop-app-beta/pull-requests/317 ships
    API.Ability()
      .get()
      .then(({ data }) => {
        this.setState({ google_enabled: data['IDENTITY_INCLUDE_GOOGLE'] });
      });

    getIdentityProviders().then(({ data }) => {
      const { href } = window.location;
      const providerState = data.providers.reduce(
        (acc, { name, url_pattern }) => ({
          ...acc,
          [`${name}_url`]: url_pattern.replace('{{ url }}', href),
        }),
        {
          google_url: null,
          facebook_url: null,
          apple_url: null,
        },
      );
      this.setState(providerState);
    });
  }

  componentWillUnmount() {
    inputs.forEach(input => {
      removeVirtualKeyboardPadding(
        this[input].current,
        () => this.resizeContainer(true),
        () => this.resizeContainer(false),
      );
    });
  }

  resizeContainer = addPadding => {
    this.setState({ addPadding });
  };

  setFactory = field => event => {
    this.setState({
      [field]: event.target.value,
      passwordIncorrect: false,
    });
  };

  validateData = () => {
    const { email, password } = this.state;
    const { location } = this.props;

    if (!email && !get(location, 'state.message')) {
      return false;
    }

    if (!password) return false;

    return true;
  };

  getConflictInfo = () => {
    const errorCode = get(this.props, 'location.state.errorCode');
    if (!errorCode) {
      return '';
    }

    switch (errorCode) {
      case EMAIL_CONFLICT: {
        return 'This email address is already being used on Quartermaster. Sign in or reset password to continue.';
      }
      case USER_PROPERTY_CONFLICT: {
        return 'It looks like we found you on this property with a different email address. If this is you, log in here!';
      }
      default: {
        return 'It looks like we found you on this property with a different email address. If this is you, log in here!';
      }
    }
  };

  postData = e => {
    e.preventDefault();
    if (!this.validateData()) return;
    const { email, password } = this.state;
    const { onSubmit, inviteData, location } = this.props;

    const options = {
      invitation_token: get(inviteData, 'token', undefined),
    };

    onSubmit(
      get(location, 'state.user.email') || email,
      password,
      options,
      ({ error }) => {
        if (error) {
          this.setState({ passwordIncorrect: true });
        }
      },
    ).catch(res => {
      if (get(res, 'data.error', null)) {
        this.setState({ passwordIncorrect: true });
      }
    });
  };

  handleSSOSignIn = data => {
    this.setState({ loadingSignOn: false }, () => {
      this.props.ssoLogin(data);
      const propertyId =
        get(data, 'user.default_property_id') ||
        get(data, 'user.properties.0.id', 0);

      window.location = `/properties/${propertyId}`;
    });
  };

  renderPasswordError = () => (
    <GenericInlineError errorDetails="You've entered an invalid email and password combination." />
  );

  renderGenericError = () => (
    <GenericInlineError
      errorDetails={
        <>
          Something went wrong. Need help?
          <a
            href={`mailto:${constants.QM_SUPPORT_EMAIL}`}
            className={containerStyles.errorText}
            style={{
              fontWeight: 'bold',
              textDecoration: 'none',
            }}
          >
            Contact Us
          </a>
        </>
      }
    />
  );

  renderUserCard = () => {
    const { location } = this.props;
    return (
      <div className={containerStyles.userCardContainer}>
        <div className={containerStyles.userCardImageWrapper}>
          <div
            className={`qm-icon-connections ${containerStyles.userCardIcon}`}
          />
        </div>
        <div className={containerStyles.userCardTextContainer}>
          <p className={containerStyles.userCardName}>
            {`${get(location.state, 'first_name', '')} ${get(
              location.state,
              'last_name',
              '',
            )}`}
          </p>
          <p className={containerStyles.userCardAddress}>
            {get(location.state, 'address_line1')}
          </p>
        </div>
      </div>
    );
  };

  renderUser = () => {
    const { location } = this.props;
    return <p>{location.state.email}</p>;
  };

  renderConflictFound = () => {
    const errorCopy = this.getConflictInfo();
    return (
      <>
        <h3 className={containerStyles.title}>Already on Quartermaster?</h3>
        <p className={containerStyles.subTitle}>{errorCopy}</p>
      </>
    );
  };

  renderSigninExisting = () => (
    <>
      <span
        style={{
          borderTop: `1px solid ${qmStyles.borderColor}`,
          width: '100%',
          marginBottom: qmStyles.oneHalfGutter,
        }}
      />
      <p
        style={{
          marginBottom: qmStyles.gutter,
          fontSize: '1.4rem',
          fontWeight: 'normal',
          lineHeight: 'normal',
        }}
      >
        Or Sign in with
      </p>
      {this.state.apple_url && (
        <>
          <SSOContainer
            serviceName="apple"
            signOnText="Sign in with Apple"
            redirectUri={`${constants.BASE_URL}/app/login`}
            url={this.state.apple_url}
            onSuccess={this.handleSSOSignIn}
            onNeedsSignup={() => {
              this.props.history.replace(`/signup/info`);
            }}
            renderButton={({ href, onClick, isConnected, signOnText }) => (
              <Apple
                href={href}
                onClick={onClick}
                isConnected={isConnected}
                signOnText={signOnText}
              />
            )}
          />
          <span style={{ marginTop: qmStyles.gutter }} />
        </>
      )}
      {this.state.google_enabled && this.state.google_url && (
        <>
          <SSOContainer
            serviceName="google"
            signOnText="Sign in with Google"
            redirectUri={`${constants.BASE_URL}/app/login`}
            url={this.state.google_url}
            onSuccess={this.handleSSOSignIn}
            onNeedsSignup={() => {
              this.props.history.replace(`/signup/info`);
            }}
            renderButton={({ href, onClick, isConnected, signOnText }) => (
              <Google
                href={href}
                onClick={onClick}
                isConnected={isConnected}
                signOnText={signOnText}
              />
            )}
          />
          <span style={{ marginTop: qmStyles.gutter }} />
        </>
      )}
      {this.state.facebook_url && (
        <SSOContainer
          serviceName="facebook"
          signOnText="Sign in with Facebook"
          redirectUri={`${constants.BASE_URL}/app/login`}
          url={this.state.facebook_url}
          onSuccess={this.handleSSOSignIn}
          onNeedsSignup={() => {
            this.props.history.replace(`/signup/info`);
          }}
          renderButton={({ href, onClick, isConnected, signOnText }) => (
            <Facebook
              href={href}
              onClick={onClick}
              isConnected={isConnected}
              signOnText={signOnText}
            />
          )}
        />
      )}
      <span style={{ marginBottom: qmStyles.gutter }} />
    </>
  );

  render() {
    const { location, history, navigateNext } = this.props;
    const { email, password, passwordIncorrect, addPadding } = this.state;

    return (
      <div
        className={containerStyles.container}
        style={{ paddingBottom: addPadding ? 250 : 0 }}
      >
        <form
          onSubmit={debounceWrapper(this.postData)}
          className={containerStyles.formContainer}
        >
          {this.state.loadingSignOn && (
            <div className={containerStyles.loaderContainer}>
              <Loader />
            </div>
          )}
          {get(location, 'state.message') ? (
            <>
              {this.renderConflictFound()}
              {this.renderUserCard()}
            </>
          ) : (
            <FormInput
              ref={this.email}
              type="email"
              name="email"
              label="Email"
              value={email}
              onChange={this.setFactory('email')}
              style={{ marginBottom: qmStyles.gutter }}
            />
          )}
          <SecuredFormInput
            ref={this.password}
            value={password}
            label="Password"
            onChange={this.setFactory('password')}
            style={{
              marginBottom: qmStyles.gutter,
            }}
            error={passwordIncorrect}
          />
          {passwordIncorrect && this.renderPasswordError()}

          <Clickable
            onClick={() => {
              window.location = `${
                constants.WORKING_CONFIG.BASE_URL
              }/password/reset`;
            }}
            style={{
              marginBottom: qmStyles.gutter,
              backgroundColor: 'transparent',
              marginLeft: 'auto',
            }}
          >
            <span className="text-14" style={{ color: qmStyles.qmBabyGreen }}>
              I forgot my password
            </span>
          </Clickable>
          {this.state.signInError && this.renderGenericError()}
          <FormInput
            type="submit"
            disabled={!this.validateData()}
            value={get(location, 'state.message') ? 'Next' : 'Sign In'}
            style={{
              backgroundColor: this.validateData()
                ? qmStyles.qmBabyGreen
                : qmStyles.qmDisabledGreen,
              color: 'white',
              marginBottom: qmStyles.gutter,
              width: '100%',
              border: 'none',
              height: 48,
            }}
          />
          {this.renderSigninExisting()}
          <NewAccountFooter
            history={history}
            userData={location.state}
            navigateNext={navigateNext}
          />
        </form>
      </div>
    );
  }
}

const bindAction = {
  ssoLogin,
};

const mapStateToProps = state => ({
  me: selectMe(state),
  userData: selectSignUpUser(state),
  inviteData: selectSignUpInvite(state),
});

export default withRouter(
  connect(
    mapStateToProps,
    bindAction,
  )(SignInForm),
);
